// @noflow
/* global Conviva */

import { isEmpty } from 'lodash';
import { SourceType } from 'Common/models/franchise';
import PlayerManager from '../PlayerManager';
import { PlayerEvent } from '../../../player/utils';

class ConvivaManager extends PlayerManager {
  constructor(playerDelegate, listing, browserFingerprint) {
    super(playerDelegate);
    this._listing = listing || this._playerProps.dvrListing;
    this._browserFingerprint = browserFingerprint;
    this._streamType =
      this._playerProps.stream?.airing_type ||
      this._playerProps.stream?.airingType ||
      (this.sourceType === 'dvr' ? 'dvrSling' : 'VOD');
  }

  get enabled() {
    return this._videoMetadata ? !!this._videoMetadata.custom_attributes.conviva_enabled : false;
  }

  get events() {
    return {
      [PlayerEvent.SourceLoaded]: () => {
        this.initialize();
      },
      [PlayerEvent.Error]: () => {
        this._client.reportError(this._session, 'error', Conviva.Client.ErrorSeverity.FATAL);
      },
      [PlayerEvent.VideoPlaybackQualityChanged]: () => {
        const bitrate = Math.round(this._delegate.videoData.bitrate / 1000);
        this._playerStateManager.setBitrateKbps(bitrate);
      },
      [PlayerEvent.Destroy]: () => {
        // need to refactor player to use sourceunloaded here
        if (this._client && typeof this._session === 'number') {
          this._client.detachPlayer(this._session);
        }
        this._cleanUp();
        this._client.release();
      },
      [PlayerEvent.SourceUnloaded]: () => {
        if (this._client && typeof this._session === 'number') {
          this._client.detachPlayer(this._session);
        }
        this._cleanUp();
      },
      [PlayerEvent.PlaybackFinished]: () => {
        this._cleanUp();
      },
    };
  }

  async initialize() {
    this._cleanUp();

    const systemSettings = new Conviva.SystemSettings();
    // if (!Config.base.env == 'production')
    systemSettings.logLevel = Conviva.SystemSettings.LogLevel.DEBUG; // to enable logging
    const systemInterface = new Conviva.SystemInterface(
      new Conviva.Impl.Html5Time(),
      new Conviva.Impl.Html5Timer(),
      new Conviva.Impl.Html5Http(),
      new Conviva.Impl.Html5Storage(),
      new Conviva.Impl.Html5Metadata(),
      new Conviva.Impl.Html5Logging(),
    );
    this._systemFactory = new Conviva.SystemFactory(systemInterface, systemSettings);
    const clientSettings = new Conviva.ClientSettings(
      this._videoMetadata.custom_attributes?.conviva_customer_key,
    );
    clientSettings.gatewayUrl = this._videoMetadata.custom_attributes?.conviva_service_url; // test url - 'https://echostar-dany.testonly.conviva.com';
    this._client = new Conviva.Client(clientSettings, this._systemFactory);

    const contentMetadata = await this.createMetadataObject();
    if (!contentMetadata) return;

    const session = this._client.createSession(contentMetadata);

    if (session !== Conviva.Client.NO_SESSION_KEY) {
      this._session = session;
      this._playerStateManager = this._client.getPlayerStateManager();
      this._client.attachPlayer(this._session, this._playerStateManager);
      this._html5PlayerInterface = new Conviva.Impl.Html5PlayerInterface(
        this._playerStateManager,
        this._delegate.videoElement,
        this._systemFactory,
      );
    }
  }

  async createMetadataObject() {
    // TODO: implement assetName (and streamType) for live and episodes once we have content
    // Movie Pattern: [{contentId}] {Movie Title}
    // Sample Value: [12345] The ABC Movie
    // Episode Pattern: [{contentId}] {Show Title} - S:{Season Number}:E{Episode Number} - {Episode Title}
    // Sample Value: [67890] The XYZ Show - S3:E1 - The Pilot Episode
    // Live Stream Pattern: [{channelNumber}] {Chanel Name}
    // Sample Value: [10] PQRS Bay Area
    const playerProps = this._playerProps;
    const airing = (this._airing && this._airing.airing) || {};
    const qvt = (this._airing && this._airing.qvt) || {};
    const franchise = this._franchise || this._listing?._franchise;
    const listing = this._listing;

    if (!franchise && !listing) {
      console.warn('Must provide franchise and listing information');
      return undefined;
    }

    const episodeTitle =
      airing?.episode_title || playerProps?.stream?.episode_title || listing?.title || '';
    const channelId = qvt?.playback_info?.linear_info?.channel_id || 'no_channel_id';
    const episodeSeason = playerProps?.stream?.episode_season || listing?.season || '';
    const episodeNumber = playerProps?.stream?.episode_number || listing?.episodeNumber || '';
    const networkName =
      airing?.ott_mapping_id ||
      listing?.dynaStream?.network_name ||
      qvt?.playback_info?.linear_info?.channel_id ||
      playerProps?.channel?.callsign ||
      listing?.channel ||
      undefined;

    if (!networkName) {
      return undefined;
    }

    const contentMetadata = new Conviva.ContentMetadata();
    contentMetadata.streamUrl = this._delegate.streamUrl || '';
    contentMetadata.assetName =
      this._streamType === 'linear'
        ? `[${channelId}: live_linear] ${airing.name} - ${episodeTitle}`
        : this._playerType === 'live'
        ? `[${channelId}:P] ${airing.name} - ${episodeTitle}`
        : franchise.kind === 'movie'
        ? `[${this._videoId}:P] ${franchise.name}`
        : `[${this._videoId}:P] ${
            franchise.name
          } - S${episodeSeason}:E${episodeNumber} - ${episodeTitle}`;
    contentMetadata.streamType =
      this._streamType === 'linear'
        ? Conviva.ContentMetadata.StreamType.LIVE
        : Conviva.ContentMetadata.StreamType.VOD; // UNKNOWN/LIVE/VOD
    contentMetadata.applicationName = this._delegate.playerName;
    contentMetadata.viewerId = this._delegate.userId || 'Guest';
    contentMetadata.duration =
      this._playerType === 'live' && !isEmpty(airing)
        ? airing.duration
        : playerProps.stream?.run_time || listing.duration;
    contentMetadata.encodedFrameRate = 'NA';

    const customMetadata = // ALL VALUES HAVE TO BE STRINGS
      this._streamType === 'linear'
        ? {
            abTest: 'Insights',
            channel: networkName,
            contentID: airing.content_identification.echostar_id,
            contentType: franchise.kind,
            duration: this._delegate?.duration?.toString(),
            quality: 'HQ',
            genre: airing.genres.length ? airing.genres[0] : 'NA',
            episodeName: airing.kind === 'show' ? airing.episode_title : 'NA',
            rating: airing.rating,
            releaseDate: airing.original_air_date,
            show: airing.name,
            protocol: this._delegate.streamProtocol,
            drm: await this._delegate.streamDrm(),
            customDma: this._browserFingerprint,
            playerVersion: this._delegate.playerVersion,
            streamType: this._streamType,
          }
        : {
            abTest: 'Insights',
            channel: networkName,
            contentID: `${this._videoId}`,
            contentType: franchise.kind,
            duration:
              this._delegate?.duration?.toString() || this._airing?.airing?.run_time?.toString(),
            quality: 'HQ',
            genre: franchise.genres ? franchise.genres[0] : '',
            partner: this._videoMetadata?.mediaviewPartnerId?.toString(),
            rating: franchise.rating ? franchise.rating[0] : '',
            releaseDate: franchise.release_year?.toString(),
            show: franchise.name,
            episodeName: franchise.kind === 'show' ? listing?.title : 'NA',
            protocol: this._delegate.streamProtocol,
            drm: await this._delegate.streamDrm(),
            customDma: this._browserFingerprint,
            playerVersion: this._delegate.playerVersion,
            streamType: this._streamType,
          };
    contentMetadata.custom = { ...customMetadata };
    return contentMetadata;
  }

  _cleanUp() {
    if (typeof this._session !== 'undefined') {
      this._client.cleanupSession(this._session);
      this._client.releasePlayerStateManager(this._playerStateManager);
      this._html5PlayerInterface.cleanup();
      this._systemFactory.release();
      delete this._session;
    }
  }
}

export default ConvivaManager;
